import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  structuredData,
  page = 'home', // Page identifier to fetch from database
}) => {
  const seoSettings = useSelector((state) => state.seo.settings[page]);
  const settings = useSelector((state) => state.settings.data);
  
  const companyName = settings?.companyName || 'Store';
  const siteUrl = settings?.websiteUrl || process.env.REACT_APP_SITE_URL || window.location.origin;
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const siteName = companyName;
  const defaultTitle = siteName;
  const defaultDescription = settings?.joinOurJourney?.description || 'Discover our collection';
  const defaultKeywords = 'products, shop, online store';
  const defaultAuthor = companyName;

  // Determine if this is a dynamic page (product-detail, blog-detail) that should prioritize props
  // For dynamic pages: Product/blog-specific data takes priority over template SEO
  // For template pages: Database SEO takes priority over props
  const isDynamicPage = page === 'product-detail' || page === 'blog-detail';
  
  // Priority logic:
  // Dynamic pages: props > database SEO > defaults
  // Template pages: database SEO > props > defaults
  const pageTitle = isDynamicPage
    ? (title ? `${title} | ${siteName}` : (seoSettings?.isActive && seoSettings?.title ? seoSettings.title : defaultTitle))
    : (seoSettings?.isActive && seoSettings?.title ? seoSettings.title : (title ? `${title} | ${siteName}` : defaultTitle));
  
  const pageDescription = isDynamicPage
    ? (description || (seoSettings?.isActive && seoSettings?.description ? seoSettings.description : defaultDescription))
    : (seoSettings?.isActive && seoSettings?.description ? seoSettings.description : (description || defaultDescription));
  
  const pageKeywords = isDynamicPage
    ? (keywords || (seoSettings?.isActive && seoSettings?.keywords ? seoSettings.keywords : defaultKeywords))
    : (seoSettings?.isActive && seoSettings?.keywords ? seoSettings.keywords : (keywords || defaultKeywords));
  const ogTitle = seoSettings?.isActive && seoSettings?.ogTitle 
    ? seoSettings.ogTitle 
    : pageTitle;
  const ogDescription = seoSettings?.isActive && seoSettings?.ogDescription 
    ? seoSettings.ogDescription 
    : pageDescription;
  const ogImage = seoSettings?.isActive && seoSettings?.ogImage 
    ? seoSettings.ogImage 
    : (image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : `${siteUrl}/og-image.jpg`);
  const twitterTitle = seoSettings?.isActive && seoSettings?.twitterTitle 
    ? seoSettings.twitterTitle 
    : pageTitle;
  const twitterDescription = seoSettings?.isActive && seoSettings?.twitterDescription 
    ? seoSettings.twitterDescription 
    : pageDescription;
  const twitterImage = seoSettings?.isActive && seoSettings?.twitterImage 
    ? seoSettings.twitterImage 
    : ogImage;
  const canonicalUrl = seoSettings?.isActive && seoSettings?.canonicalUrl 
    ? seoSettings.canonicalUrl 
    : fullUrl;
  const robots = seoSettings?.isActive && seoSettings?.robots 
    ? seoSettings.robots 
    : 'index, follow';
  const finalStructuredData = seoSettings?.isActive && seoSettings?.structuredData 
    ? seoSettings.structuredData 
    : structuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content={author || defaultAuthor} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />

      {/* Article specific */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

